import { AfterViewInit, Component, computed, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';

import { CatalogPageCardComponent } from '../shared/components/catalog-page-card';
import { CatalogDatatableController } from '../shared/catalog-datatable.controller';
import { BondsCloningService } from './bonds-cloning.service';
import {
  BondCloningExecuteRequest,
  BondCloningExecuteResponse,
  BondCloningTableRow,
} from './bonds-cloning.types';

type WizardStep = 1 | 2 | 3;

@Component({
  selector: 'app-bonds-cloning',
  imports: [CatalogPageCardComponent, RouterLink],
  templateUrl: './bonds-cloning.html',
  styleUrl: './bonds-cloning.css',
})
export class BondsCloningComponent implements AfterViewInit {
  private readonly sourceDatatableId = 'kt_bonds_cloning_source_datatable';
  private readonly targetDatatableId = 'kt_bonds_cloning_target_datatable';
  private readonly sourceDatatableController = new CatalogDatatableController(this.sourceDatatableId);
  private readonly targetDatatableController = new CatalogDatatableController(this.targetDatatableId);

  protected readonly sourceDatatableSelector = `#${this.sourceDatatableId}`;
  protected readonly targetDatatableSelector = `#${this.targetDatatableId}`;
  protected readonly step = signal<WizardStep>(1);
  protected readonly isLoadingSources = signal(false);
  protected readonly sourceError = signal('');
  protected readonly sources = signal<BondCloningTableRow[]>([]);
  protected readonly selectedSource = signal<BondCloningTableRow | null>(null);

  protected readonly isLoadingTargets = signal(false);
  protected readonly targetError = signal('');
  protected readonly targets = signal<BondCloningTableRow[]>([]);
  protected readonly selectedTarget = signal<BondCloningTableRow | null>(null);

  protected readonly isExecuting = signal(false);
  protected readonly executeError = signal('');
  protected readonly executeResult = signal<BondCloningExecuteResponse | null>(null);
  protected readonly resolvedCloneType = computed<'Clone' | 'Copy'>(() =>
    this.selectedSource()?.tradeStatus === 'CANC' ? 'Clone' : 'Copy',
  );

  constructor(
    private readonly bondsCloningService: BondsCloningService,
    private readonly router: Router,
  ) {
    this.loadSources();
  }

  ngAfterViewInit(): void {
    this.queueSourceDatatableInitialization();
  }

  protected loadSources(): void {
    this.isLoadingSources.set(true);
    this.sourceError.set('');

    this.bondsCloningService.getSources().subscribe({
      next: (rows) => {
        this.sources.set(rows);
        this.isLoadingSources.set(false);
        this.queueSourceDatatableInitialization();
      },
      error: () => {
        this.sourceError.set('Unable to load source bonds for cloning.');
        this.isLoadingSources.set(false);
      },
    });
  }

  protected loadTargets(): void {
    const source = this.selectedSource();
    if (!source) {
      return;
    }

    this.isLoadingTargets.set(true);
    this.targetError.set('');
    this.targets.set([]);

    this.bondsCloningService.getTargets(source.tradeId).subscribe({
      next: (rows) => {
        this.targets.set(rows);
        this.isLoadingTargets.set(false);
        this.queueTargetDatatableInitialization();
      },
      error: () => {
        this.targetError.set('Unable to load target bonds for cloning.');
        this.isLoadingTargets.set(false);
      },
    });
  }

  protected onSelectSource(row: BondCloningTableRow): void {
    this.selectedSource.set(row);
    this.selectedTarget.set(null);
    this.executeResult.set(null);
    this.executeError.set('');
    this.step.set(2);
    this.loadTargets();
  }

  protected onSelectTarget(row: BondCloningTableRow): void {
    this.selectedTarget.set(row);
    this.executeResult.set(null);
    this.executeError.set('');
    this.step.set(3);
  }

  protected onSourceTableActionClick(event: Event): void {
    const target = event.target as HTMLElement | null;
    const button = target?.closest('button[data-action="select-source-bond"]') as HTMLButtonElement | null;
    if (!button || button.disabled) {
      return;
    }

    const tradeId = button.getAttribute('data-source-trade-id') ?? '';
    const row = this.sources().find((item) => item.tradeId === tradeId);
    if (!row) {
      return;
    }

    this.onSelectSource(row);
  }

  protected onTargetTableActionClick(event: Event): void {
    const target = event.target as HTMLElement | null;
    const button = target?.closest('button[data-action="select-target-bond"]') as HTMLButtonElement | null;
    if (!button || button.disabled) {
      return;
    }

    const tradeId = button.getAttribute('data-target-trade-id') ?? '';
    const row = this.targets().find((item) => item.tradeId === tradeId);
    if (!row) {
      return;
    }

    this.onSelectTarget(row);
  }

  protected onBackToSourceStep(): void {
    this.step.set(1);
    this.queueSourceDatatableInitialization();
  }

  protected onBackToTargetStep(): void {
    this.step.set(2);
  }

  protected onExecute(): void {
    const source = this.selectedSource();
    const target = this.selectedTarget();
    if (!source || !target || this.isExecuting()) {
      return;
    }

    this.isExecuting.set(true);
    this.executeError.set('');

    const payload: BondCloningExecuteRequest = {
      sourceTradeId: source.tradeId,
      targetTradeId: target.tradeId,
      cloneType: this.resolvedCloneType(),
    };

    this.bondsCloningService.execute(payload).subscribe({
      next: (response) => {
        this.executeResult.set(response);
        this.isExecuting.set(false);
        window.KTToast?.show({
          message: `Bond cloning completed for target ${response.targetTradeId}.`,
          variant: 'success',
        });
        void this.router.navigate(['/bond-detail', response.targetTradeId]);
      },
      error: () => {
        this.executeError.set('Unable to execute bond cloning. Please try again.');
        this.isExecuting.set(false);
        window.KTToast?.show({
          message: 'Unable to execute bond cloning. Please try again.',
          variant: 'destructive',
        });
      },
    });
  }

  protected isCurrentStep(step: WizardStep): boolean {
    return this.step() === step;
  }

  protected formatDate(value: string): string {
    if (!value) {
      return '-';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    }).format(parsed);
  }

  protected formatDateTime(value: string): string {
    if (!value) {
      return '-';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(parsed);
  }

  protected formatNumber(value: number | null, digits = 2): string {
    if (value === null) {
      return '-';
    }

    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(value);
  }

  private queueSourceDatatableInitialization(): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.sourceDatatableController.initialize();
      });
    });
  }

  private queueTargetDatatableInitialization(): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.targetDatatableController.initialize();
      });
    });
  }
}
